import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack, HStack, View, useTheme, Box } from 'native-base';
import { chunk, range } from 'lodash';
import Ionicons from '@expo/vector-icons/Ionicons';

import { Button, Text } from '@pimm/base';
import { SecurityLock, SecurityUnlock } from '../icons';

type PinCodeProps = {
  pinLength?: number;
  headerComponent?: React.ReactNode;
  footerComponent?: React.ReactNode;
  submitLabel?: string;
  onCancel?: () => void;
  onValidate?: (pin: string) => Promise<any>;
};

type PinState = {
  status: 'idle' | 'loading' | 'failed' | 'success';
  error?: string;
  pin?: string;
};

export const PinCode = ({ pinLength = 4, headerComponent, footerComponent, ...props }: PinCodeProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [pinInput, setPinInput] = useState<PinState>({ status: 'idle' });
  const [keypads] = useState<(number | undefined)[][]>(chunk([...range(1, 10), undefined, 0, -1] as (number | undefined)[], 3));

  const pin = pinInput.pin ?? '';

  const handleKeyPress = (key: number | undefined) => {
    if (key === -1) {
      // handles x button
      setPinInput({ pin: pin?.slice(0, -1), status: 'idle' });
    } else if (key !== undefined) {
      setPinInput({ pin: pin.length < pinLength ? pin + key : pin, status: 'idle' });
    }
  };

  const handleTryValidate = async () => {
    setPinInput(prev => ({ pin: prev.pin, status: 'loading' }));

    if (props.onValidate) {
      try {
        await props.onValidate(pinInput.pin!);
        setPinInput(prev => ({ pin: prev.pin, status: 'success' }));
      } catch (error: any) {
        setPinInput({ status: 'failed', error: error.status === 400 ? 'app:invalid_pin' : 'app:something_went_wrong' });
      }
    }
  };

  return (
    <VStack space={4}>
      {/* Pin Header */}
      {headerComponent && <View>{headerComponent}</View>}

      {/* Pin Display */}
      <HStack w="full" space={3} alignItems="center" justifyContent="center" pb={pinInput.error ? 0 : 3}>
        {range(pinLength).map(_index => (
          <View
            key={`pin-${_index}`}
            style={{
              width: 22,
              height: 22,
              borderRadius: 14,
              backgroundColor: pin[_index] ? colors.black : colors.white,
              borderWidth: 2,
              borderColor: 'black',
            }}
          />
        ))}
      </HStack>

      {!!pinInput.error && (
        <HStack space={2} alignItems="center" justifyContent="center">
          <SecurityLock color="error.500" size="md" />
          <Text color={colors.error[600]} fontWeight={500} size="md">
            {t(pinInput.error)}
          </Text>
        </HStack>
      )}

      {pinInput.status === 'success' && (
        <HStack space={2} alignItems="center" justifyContent="center">
          <SecurityUnlock color="success.500" size="md" />
          <Text color={colors.success[600]} fontWeight={500} size="md">
            Success!
          </Text>
        </HStack>
      )}

      {/* Keypad */}
      <VStack w="full" space={3}>
        {keypads.map(_keypad => (
          <HStack key={`row-${_keypad}`} justifyContent="center" space={2} w="full">
            {_keypad.map((_key, _index) => (
              <Box key={`key-${_key !== undefined ? _key : `empty-${_index}`}`} w="1/3">
                {typeof _key === 'number' ? (
                  <Button
                    variant="unstyled"
                    height={46}
                    shadow={0}
                    w="full"
                    borderRadius={8}
                    borderColor={colors.gray[300]}
                    bg="white"
                    _pressed={{ bg: 'gray.50', shadow: 1 }}
                    onPress={() => handleKeyPress(_key)}
                  >
                    {_key === -1 ? (
                      <Ionicons name="backspace" size={24} color={colors.black} />
                    ) : (
                      <Text size="4xl" color={colors.black} fontWeight={500}>
                        {_key}
                      </Text>
                    )}
                  </Button>
                ) : (
                  <Text />
                )}
              </Box>
            ))}
          </HStack>
        ))}
      </VStack>

      {/* Pin Footer */}
      {footerComponent && <View>{footerComponent}</View>}

      <HStack space={2}>
        <Button variant="unstyled" flex={1} shadow={0} borderColor="gray.300" bg="white" onPress={props.onCancel}>
          {t('app:cancel')}
        </Button>

        <Button
          flex={1}
          shadow={0}
          disabled={!pinInput.pin || pinInput.pin.length !== pinLength}
          isLoading={pinInput.status === 'loading'}
          onPress={handleTryValidate}
        >
          {props?.submitLabel ?? t('app:validate')}
        </Button>
      </HStack>
    </VStack>
  );
};
